// A02 + Captain 2026-04-26: Sandbox-runtime lifecycle pill +
// click-to-open popout. Polls GET /workspaces/:id/sandbox every 5s.
// States: cold / warming / active / snapshotting / asleep.
// Click toggles a popout with the sandbox id, last heartbeat, current
// state, a Wake button, and a Settings link — same shape as
// OpenRouterBalance. The runtime is Waytide-managed; the popout never
// uses the provider's brand name in copy (Captain directive 2026-04-26).

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Box } from "lucide-react";
import {
  ApiError,
  getEntitlements,
  getWorkspaceSandbox,
  getWorkspaceSandboxDetails,
  wakeWorkspaceSandbox,
  type Entitlements,
  type SandboxDetailsResponse,
  type SandboxStatusResponse,
} from "../api/client";

interface Props {
  workspaceId: number;
}

const POLL_MS = 5000;

function formatAgo(microsTs: number): string {
  if (microsTs <= 0) return "never";
  const diffMs = Date.now() - microsTs / 1000;
  if (diffMs < 5_000) return "just now";
  if (diffMs < 60_000) return `${Math.floor(diffMs / 1000)}s ago`;
  if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m ago`;
  return `${Math.floor(diffMs / 3_600_000)}h ago`;
}

function shortId(id: string): string {
  if (!id) return "—";
  if (id.length <= 14) return id;
  return `${id.slice(0, 6)}…${id.slice(-4)}`;
}

function formatIsoAgo(iso: string | null): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const diffMs = Date.now() - t;
  if (diffMs < 5_000) return "just now";
  if (diffMs < 60_000) return `${Math.floor(diffMs / 1000)}s ago`;
  if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m ago`;
  if (diffMs < 86_400_000) return `${Math.floor(diffMs / 3_600_000)}h ago`;
  return `${Math.floor(diffMs / 86_400_000)}d ago`;
}

function formatInterval(mins: number | null): string {
  if (mins == null || mins <= 0) return "off";
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

const DETAILS_REFRESH_MS = 8000;

export function SandboxStatusPill({ workspaceId }: Props) {
  const [status, setStatus] = useState<SandboxStatusResponse | null>(null);
  const [details, setDetails] = useState<SandboxDetailsResponse | null>(null);
  const [detailsBusy, setDetailsBusy] = useState(false);
  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [wakeError, setWakeError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // When the popout opens, fetch live Daytona details (cpu/memory/
  // region/lastActivity) + entitlements (workspace budget) and refresh
  // them periodically while open. Captain canon 2026-04-26: the
  // sandbox popout surfaces the workspace budget — % of compute cap
  // used today by the working agent inside the sandbox.
  useEffect(() => {
    if (!open || workspaceId <= 0) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    async function tick() {
      setDetailsBusy(true);
      try {
        const [d, e] = await Promise.all([
          getWorkspaceSandboxDetails(workspaceId),
          getEntitlements(workspaceId).catch(() => null),
        ]);
        if (!cancelled) {
          setDetails(d);
          if (e) setEntitlements(e);
        }
      } catch {
        // Silent — popout falls back to SDB-tracked basics.
      } finally {
        if (!cancelled) setDetailsBusy(false);
      }
      if (!cancelled) {
        timer = setTimeout(tick, DETAILS_REFRESH_MS);
      }
    }
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [open, workspaceId]);

  useEffect(() => {
    if (workspaceId <= 0) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function tick() {
      try {
        const s = await getWorkspaceSandbox(workspaceId);
        if (!cancelled) setStatus(s);
      } catch {
        // Silent — pill renders nothing on failure.
      }
      if (!cancelled) {
        timer = setTimeout(tick, POLL_MS);
      }
    }

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [workspaceId]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!status) return null;

  if (!status.configured) {
    return (
      <Link
        to={`?tab=settings#sandbox`}
        className="flex items-center gap-1 text-faint transition-colors hover:text-accent"
        title="Sandbox runtime not configured."
        data-testid="sandbox-status-pill"
      >
        <AlertTriangle size={14} />
      </Link>
    );
  }

  const state = status.state;
  const isWakeable = state === "cold" || state === "asleep";
  const stateLabel =
    state === "active"
      ? "active"
      : state === "warming"
        ? "warming"
        : state === "snapshotting"
          ? "snapshotting"
          : state === "asleep"
            ? "asleep"
            : "cold";

  async function onWake() {
    if (busy) return;
    setBusy(true);
    setWakeError(null);
    try {
      const r = await wakeWorkspaceSandbox(workspaceId);
      setStatus({
        configured: true,
        sandbox_id: r.sandbox_id,
        state: r.state,
        last_heartbeat: Date.now() * 1000,
      });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      setWakeError(msg);
    } finally {
      setBusy(false);
    }
  }

  // Tint the icon by state — same 14px footprint as the theme + user
  // icons next to it. No separate dot.
  const iconTone = open
    ? "text-primary"
    : state === "active"
      ? "text-accent hover:text-primary"
      : state === "warming" || state === "snapshotting"
        ? "text-warning animate-pulse hover:text-primary"
        : "text-faint hover:text-primary";

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`relative flex cursor-pointer items-center transition-colors ${iconTone}`}
        data-testid="sandbox-status-pill"
        title={busy ? "Waking sandbox…" : `Sandbox · ${stateLabel}`}
        aria-label={`Sandbox ${stateLabel}`}
      >
        <Box size={14} />
        {open && (
          <span
            aria-hidden
            className="absolute -bottom-2 left-0 right-0 h-px bg-accent"
          />
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 z-50 mt-3 w-64 rounded-md border border-edge bg-surface-raised p-3 shadow-[var(--wt-shadow)]"
          data-testid="sandbox-status-popout"
          role="menu"
        >
          <div className="mb-2 flex items-baseline justify-between">
            <span className="text-xs font-medium text-muted">State</span>
            <span className="font-code text-sm text-primary">{state}</span>
          </div>
          <div className="flex items-baseline justify-between border-t border-edge pt-2 text-xs">
            <span className="text-muted">Sandbox id</span>
            <span
              className="font-code text-primary"
              title={status.sandbox_id || "not provisioned"}
            >
              {shortId(status.sandbox_id)}
            </span>
          </div>
          <div className="mt-1 flex items-baseline justify-between text-xs">
            <span className="text-muted">Last heartbeat</span>
            <span className="font-code text-primary">
              {formatAgo(status.last_heartbeat)}
            </span>
          </div>
          {/* Workspace budget — % of daily compute cap used by the
           * working agent inside this workspace's sandbox. Captain
           * directive 2026-04-26: this is what to cut off on, not a
           * top-bar credits chip. */}
          {entitlements && (
            <div className="mt-1 flex items-baseline justify-between text-xs">
              <span className="text-muted">Today's budget</span>
              <span
                className={`font-code ${
                  entitlements.daily_compute_minutes_used >=
                  entitlements.daily_compute_minutes_cap
                    ? "text-danger"
                    : "text-primary"
                }`}
                title={`${entitlements.daily_compute_minutes_used}/${entitlements.daily_compute_minutes_cap} min · tier=${entitlements.tier}`}
              >
                {entitlements.daily_compute_minutes_used}
                {" / "}
                {entitlements.daily_compute_minutes_cap}
                {" min"}
              </span>
            </div>
          )}
          {details && details.id && (
            <>
              {(details.cpu != null || details.memory != null || details.disk != null) && (
                <div className="mt-1 flex items-baseline justify-between text-xs">
                  <span className="text-muted">Resources</span>
                  <span className="font-code text-primary">
                    {[
                      details.cpu != null ? `${details.cpu}vCPU` : null,
                      details.memory != null ? `${details.memory}GB` : null,
                      details.disk != null ? `${details.disk}GB disk` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </span>
                </div>
              )}
              {details.target && (
                <div className="mt-1 flex items-baseline justify-between text-xs">
                  <span className="text-muted">Region</span>
                  <span className="font-code text-primary">{details.target}</span>
                </div>
              )}
              {details.snapshotName && (
                <div className="mt-1 flex items-baseline justify-between text-xs">
                  <span className="text-muted">Snapshot</span>
                  <span
                    className="font-code text-primary"
                    title={details.snapshotName}
                  >
                    {details.snapshotName.length > 20
                      ? `${details.snapshotName.slice(0, 18)}…`
                      : details.snapshotName}
                  </span>
                </div>
              )}
              {details.lastActivityAt && (
                <div className="mt-1 flex items-baseline justify-between text-xs">
                  <span className="text-muted">Last activity</span>
                  <span className="font-code text-primary">
                    {formatIsoAgo(details.lastActivityAt)}
                  </span>
                </div>
              )}
              {details.autoStopInterval != null && (
                <div className="mt-1 flex items-baseline justify-between text-xs">
                  <span className="text-muted">Auto-stop after</span>
                  <span className="font-code text-primary">
                    {formatInterval(details.autoStopInterval)}
                  </span>
                </div>
              )}
              {details.errorReason && (
                <div className="mt-1 flex items-baseline justify-between gap-2 text-xs">
                  <span className="text-muted">Error</span>
                  <span
                    className="font-code text-danger"
                    title={details.errorReason}
                  >
                    {details.errorReason.length > 24
                      ? `${details.errorReason.slice(0, 22)}…`
                      : details.errorReason}
                  </span>
                </div>
              )}
            </>
          )}
          {detailsBusy && !details && (
            <p className="mt-1 text-[0.625rem] text-faint">loading details…</p>
          )}
          <div className="mt-3 flex items-center gap-2 border-t border-edge pt-2">
            <button
              type="button"
              disabled={!isWakeable || busy}
              onClick={() => void onWake()}
              className="flex-1 cursor-pointer rounded-md border border-edge bg-surface px-2 py-1.5 text-xs text-muted transition-colors hover:border-accent hover:text-primary disabled:cursor-default disabled:opacity-50"
              data-testid="sandbox-wake-button"
              title={
                isWakeable
                  ? "Wake the sandbox now."
                  : "Sandbox is already up; it'll snapshot itself after idle."
              }
            >
              {busy ? "waking…" : isWakeable ? "Wake now" : "Already up"}
            </button>
          </div>
          {wakeError && (
            <p className="mt-2 text-[0.625rem] text-danger" role="alert">
              {wakeError}
            </p>
          )}
          <div className="mt-3 flex items-center justify-between gap-2 border-t border-edge pt-2 text-[0.625rem] text-faint">
            <Link
              to="?tab=settings#sandbox"
              className="cursor-pointer text-muted transition-colors hover:text-primary"
            >
              Settings →
            </Link>
            <span className="text-faint">runtime · Waytide-managed</span>
          </div>
        </div>
      )}
    </div>
  );
}
